import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { productStockApi } from '../../api/productStock.js';
import { productsApi } from '../../api/products.js';
import { leadsApi } from '../../api/leads.js';

const TYPE_OPTIONS = [
  { value: '',        label: '—' },
  { value: 'printed',  label: 'Printed' },
  { value: 'normal',   label: 'Normal' },
];

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ══════════════════════════════════════════
   ADD / EDIT MODAL
══════════════════════════════════════════ */
const AddEditModal = ({ stock, onClose, onSuccess }) => {
  const isEdit = !!stock;

  const [form, setForm] = useState({
    name: stock?.name || '',
    quantity: stock?.quantity != null ? stock.quantity : 0,
    type: stock?.type || '',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState(
    stock?.products ? stock.products.map(p => p.id) : []
  );

  const [selectedCustomers, setSelectedCustomers] = useState(stock?.customers || []);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCustomerResults, setShowCustomerResults] = useState(false);

  useEffect(() => {
    setLoadingProducts(true);
    productsApi.getAll({ limit: 500, sortBy: 'name', sortOrder: 'ASC' })
      .then(res => setAllProducts(res?.data || []))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    if (!customerSearch.trim()) { setCustomerResults([]); return; }
    setLoadingCustomers(true);
    const t = setTimeout(() => {
      leadsApi.getAll({ search: customerSearch.trim(), limit: 10 })
        .then(res => setCustomerResults(res?.data || []))
        .catch(() => {})
        .finally(() => setLoadingCustomers(false));
    }, 350);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Stock item name is required.';
    if (form.quantity === '' || isNaN(Number(form.quantity)) || Number(form.quantity) < 0) {
      e.quantity = 'Quantity must be a non-negative number.';
    }
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        quantity: Number(form.quantity),
        product_ids: selectedProductIds,
        customer_ids: selectedCustomers.map(c => c.id),
        type: form.type || null,
      };
      if (isEdit) {
        await productStockApi.update(stock.id, payload);
        toast.success('Stock item updated!');
      } else {
        await productStockApi.create(payload);
        toast.success('Stock item created!');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || `Failed to ${isEdit ? 'update' : 'create'} stock item.`);
    } finally { setSaving(false); }
  };

  const filteredProducts = allProducts.filter(p =>
    (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{isEdit ? 'Edit Stock Item' : 'Create Stock Item'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: 540, overflowY: 'auto' }}>

            <div className="form-group">
              <label className="form-label">Stock Item Name *</label>
              <input className={`form-control${errors.name ? ' is-invalid' : ''}`} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. P1 ROLL S" />
              {errors.name && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <input type="number" min="0" className={`form-control${errors.quantity ? ' is-invalid' : ''}`} value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="0" />
              {errors.quantity && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.quantity}</div>}
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Customers <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(Optional — multiple customers can consume the same stock)</span></label>
                {selectedCustomers.length > 0 && (
                  <span style={{ background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, border: '1px solid #c7d2fe' }}>
                    {selectedCustomers.length} Linked
                  </span>
                )}
              </div>

              {selectedCustomers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {selectedCustomers.map(c => (
                    <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '3px 4px 3px 8px', borderRadius: 6, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe' }}>
                      {c.name}
                      <button type="button" onClick={() => setSelectedCustomers(prev => prev.filter(x => x.id !== c.id))} style={{ width: 16, height: 16, borderRadius: 99, border: 'none', background: 'rgba(99,102,241,0.15)', color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 }}>✕</button>
                    </span>
                  ))}
                </div>
              )}

              <input
                className="form-control"
                value={customerSearch}
                onChange={e => { setCustomerSearch(e.target.value); setShowCustomerResults(true); }}
                onFocus={() => setShowCustomerResults(true)}
                placeholder="Search customer by name…"
              />
              {showCustomerResults && customerSearch.trim() && (
                <div style={{ position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 180, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  {loadingCustomers ? (
                    <div style={{ padding: 10, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>Searching…</div>
                  ) : customerResults.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No customers found.</div>
                  ) : customerResults.map(c => {
                    const isSel = selectedCustomers.some(x => x.id === c.id);
                    return (
                      <div key={c.id}
                        onClick={() => { if (!isSel) setSelectedCustomers(prev => [...prev, c]); setCustomerSearch(''); setShowCustomerResults(false); }}
                        style={{ padding: '7px 10px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: isSel ? '#f8fafc' : 'transparent', color: isSel ? '#94a3b8' : '#1e293b' }}
                        onMouseDown={e => e.preventDefault()}
                      >
                        {c.name}{isSel ? ' (already added)' : ''}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Type <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(Optional)</span></label>
              <select className="form-control" value={form.type} onChange={e => set('type', e.target.value)}>
                {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ marginTop: 20, borderTop: '1.5px solid #e2e8f0', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0, fontWeight: 700 }}>
                  Linked Products <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(Optional)</span>
                </label>
                {selectedProductIds.length > 0 && (
                  <span style={{ background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, border: '1px solid #c7d2fe' }}>
                    ✓ {selectedProductIds.length} Linked
                  </span>
                )}
              </div>

              <div style={{ position: 'relative', marginBottom: 8 }}>
                <input className="form-control" style={{ fontSize: '0.8rem' }} value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products by name or SKU…" />
                {productSearch && <button type="button" onClick={() => setProductSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>✕</button>}
              </div>

              {loadingProducts ? (
                <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>Loading products…</div>
              ) : allProducts.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '8px 0' }}>No products in catalog.</div>
              ) : (
                <div style={{ maxHeight: 220, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                  {filteredProducts.map(p => {
                    const isSel = selectedProductIds.includes(p.id);
                    return (
                      <div key={p.id} onClick={() => setSelectedProductIds(ids => isSel ? ids.filter(id => id !== p.id) : [...ids, p.id])}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSel ? '#eef2ff' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={isSel} onChange={() => {}} style={{ width: 14, height: 14, accentColor: '#6366f1' }} />
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isSel ? '#6366f1' : '#1e293b' }}>{p.name}</span>
                          {p.sku && <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#94a3b8', background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, border: '1px solid #e2e8f0' }}>{p.sku}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {filteredProducts.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8' }}>No matching products.</div>}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MANAGE LINKED PRODUCTS MODAL
══════════════════════════════════════════ */
const ManageLinksModal = ({ stock, onClose, onSave }) => {
  const [selectedProductIds, setSelectedProductIds] = useState(stock.products?.map(p => p.id) || []);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    setLoadingProducts(true);
    productsApi.getAll({ limit: 500, sortBy: 'name', sortOrder: 'ASC' })
      .then(res => setAllProducts(res?.data || []))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, []);

  const removeProduct = (id) => setSelectedProductIds(ids => ids.filter(x => x !== id));
  const toggleProduct = (id) => setSelectedProductIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const filteredProducts = allProducts.filter(p =>
    (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span style={{ fontWeight: 700 }}>Linked Products</span>
            <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: 12 }}>{stock.name}</p>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: 480, overflowY: 'auto' }}>
          {selectedProductIds.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label className="form-label" style={{ marginBottom: 6 }}>Currently linked ({selectedProductIds.length})</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedProductIds.map(id => {
                  const p = allProducts.find(pr => pr.id === id) || stock.products?.find(pr => pr.id === id);
                  if (!p) return null;
                  return (
                    <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '3px 4px 3px 8px', borderRadius: 6, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe' }}>
                      {p.name}
                      <button type="button" onClick={() => removeProduct(id)} style={{ width: 16, height: 16, borderRadius: 99, border: 'none', background: 'rgba(99,102,241,0.15)', color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 }}>✕</button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <label className="form-label" style={{ marginBottom: 6 }}>Add a product</label>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input className="form-control" style={{ fontSize: '0.8rem' }} value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products by name or SKU…" />
          </div>
          {loadingProducts ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>Loading products…</div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '8px 0' }}>No matching products.</div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
              {filteredProducts.map(p => {
                const isSel = selectedProductIds.includes(p.id);
                return (
                  <div key={p.id} onClick={() => toggleProduct(p.id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSel ? '#eef2ff' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={isSel} onChange={() => {}} style={{ width: 14, height: 14, accentColor: '#6366f1' }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isSel ? '#6366f1' : '#1e293b' }}>{p.name}</span>
                      {p.sku && <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#94a3b8', background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, border: '1px solid #e2e8f0' }}>{p.sku}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => onSave(selectedProductIds)}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ProductStockPage() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputSearch, setInputSearch] = useState('');
  const debouncedSearch = useDebounce(inputSearch, 400);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [limit, setLimit] = useState(20);
  const [adjust, setAdjust] = useState({}); // { [stockId]: { in: '', out: '' } }
  const [linksTarget, setLinksTarget] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState({}); // { [stockId]: string }
  const [remarkEditing, setRemarkEditing] = useState({}); // { [stockId]: true }

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productStockApi.getAll({ page, limit: limit === 100000 ? 100000 : limit, search: debouncedSearch || undefined });
      if (res?.success) {
        setStocks(res.data || []);
        setTotalCount(res.count || 0);
        setTotalPages(res.totalPages || 1);
      }
    } catch { toast.error('Failed to load stock items.'); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This will remove all its product links. This cannot be undone!`)) return;
    try {
      await productStockApi.delete(item.id);
      toast.success('Stock item deleted!');
      fetchList();
    } catch { toast.error('Failed to delete.'); }
  };

  const handleSaveLinks = async (product_ids) => {
    try {
      await productStockApi.update(linksTarget.id, { product_ids });
      toast.success('Linked products updated!');
      setLinksTarget(null);
      fetchList();
    } catch { toast.error('Failed to update linked products.'); }
  };

  const setAdjustField = (id, field, value) =>
    setAdjust(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const applyAdjust = async (stock) => {
    const inVal = Number(adjust[stock.id]?.in || 0);
    const outVal = Number(adjust[stock.id]?.out || 0);
    if (!inVal && !outVal) return;
    const newQuantity = Math.max(0, stock.quantity + inVal - outVal);

    const parts = [];
    if (inVal) parts.push(`+${inVal} in`);
    if (outVal) parts.push(`-${outVal} out`);
    const confirmed = window.confirm(
      `Apply ${parts.join(', ')} to "${stock.name}"?\n\nCurrent quantity: ${stock.quantity}\nNew quantity: ${newQuantity}`
    );
    if (!confirmed) return;

    try {
      await productStockApi.update(stock.id, { quantity: newQuantity });
      toast.success('Stock quantity updated!');
      setAdjust(prev => ({ ...prev, [stock.id]: { in: '', out: '' } }));
      fetchList();
    } catch { toast.error('Failed to update stock quantity.'); }
  };

  const getRemark = (s) => remarkDraft[s.id] ?? s.remark ?? '';
  const isEditingRemark = (s) => !s.remark || remarkEditing[s.id];
  const hasRemarkChange = (s) => getRemark(s).trim() !== (s.remark || '');
  const startEditRemark = (s) => setRemarkEditing(prev => ({ ...prev, [s.id]: true }));

  const saveRemark = async (s) => {
    const value = getRemark(s).trim();
    if (value === (s.remark || '')) return;
    try {
      await productStockApi.update(s.id, { remark: value });
      setRemarkEditing(prev => ({ ...prev, [s.id]: false }));
      fetchList();
    } catch { toast.error('Failed to update remark.'); }
  };

  return (
    <div className="page-body">
      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-title">Product Stock</span>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>{totalCount} stock items · linked to catalog products</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditTarget(null); setShowModal(true); }}>+ Add Stock Item</button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
            <input style={{ paddingLeft: 32 }} className="form-control" value={inputSearch} onChange={e => setInputSearch(e.target.value)} placeholder="Search stock items by name…" />
          </div>
        </div>

        <div className="card-body" style={{ padding: 0 }}>
          {loading && stocks.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : stocks.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
              <p style={{ fontWeight: 600, margin: '0 0 4px' }}>No stock items found</p>
              <p style={{ fontSize: 13, margin: '0 0 12px' }}>Create a stock item and link it to catalog products.</p>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditTarget(null); setShowModal(true); }}>+ Add Stock Item</button>
            </div>
          ) : (
            <div className="table-wrapper" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>ID</th>
                    <th>Stock Item</th>
                    <th>Quantity</th>
                    <th>Adjust Stock</th>
                    <th>Linked Products</th>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Remark</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map(item => {
                    const inVal = Number(adjust[item.id]?.in || 0);
                    const outVal = Number(adjust[item.id]?.out || 0);
                    const hasAdjust = inVal > 0 || outVal > 0;
                    const previewQty = Math.max(0, item.quantity + inVal - outVal);
                    return (
                    <tr key={item.id}>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{item.id}</td>
                      <td style={{ fontWeight: 600 }}>{item.name}</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{item.quantity}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                          <div>
                            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 2 }}>IN</label>
                            <input
                              type="number" min="0" placeholder="0"
                              value={adjust[item.id]?.in ?? ''}
                              onChange={e => setAdjustField(item.id, 'in', e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && applyAdjust(item)}
                              style={{ width: 56, padding: '4px 6px', fontSize: 12, textAlign: 'center', border: '1px solid #a7f3d0', borderRadius: 6 }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#dc2626', marginBottom: 2 }}>OUT</label>
                            <input
                              type="number" min="0" placeholder="0"
                              value={adjust[item.id]?.out ?? ''}
                              onChange={e => setAdjustField(item.id, 'out', e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && applyAdjust(item)}
                              style={{ width: 56, padding: '4px 6px', fontSize: 12, textAlign: 'center', border: '1px solid #fecaca', borderRadius: 6 }}
                            />
                          </div>
                          {hasAdjust && (
                            <button className="btn btn-primary btn-xs" style={{ whiteSpace: 'nowrap' }} onClick={() => applyAdjust(item)}>
                              Save → {previewQty.toLocaleString()}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {item.products && item.products.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {item.products.map(p => (
                                <span key={p.id} style={{ fontSize: 11, padding: '2px 6px', background: '#eef2ff', color: '#6366f1', borderRadius: 4, border: '1px solid #c7d2fe' }}>{p.name}</span>
                              ))}
                            </div>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                          <button className="btn btn-xs btn-outline" title="Manage linked products" onClick={() => setLinksTarget(item)}>✎</button>
                        </div>
                      </td>
                      <td>
                        {item.customers && item.customers.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {item.customers.map(c => (
                              <span key={c.id} style={{ fontSize: 11, fontWeight: 600, padding: '2px 6px', background: '#f1f5f9', color: '#334155', borderRadius: 4, border: '1px solid #e2e8f0' }}>{c.name}</span>
                            ))}
                          </div>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td>
                        {item.type ? (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, textTransform: 'capitalize', background: item.type === 'printed' ? '#fef3c7' : '#dcfce7', color: item.type === 'printed' ? '#92400e' : '#166534' }}>{item.type}</span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td>
                        {isEditingRemark(item) ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="text"
                              autoFocus={!!remarkEditing[item.id]}
                              placeholder="e.g. 50 units damaged"
                              value={getRemark(item)}
                              onChange={e => setRemarkDraft(prev => ({ ...prev, [item.id]: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && saveRemark(item)}
                              style={{ width: 170, padding: '4px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            />
                            {hasRemarkChange(item) && (
                              <button className="btn btn-primary btn-xs" onClick={() => saveRemark(item)}>Save</button>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.remark}>{item.remark}</span>
                            <button className="btn btn-xs btn-outline" title="Edit remark" onClick={() => startEditRemark(item)}>✎</button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-xs btn-outline" title="Edit" onClick={() => { setEditTarget(item); setShowModal(true); }}>✎</button>
                          <button className="btn btn-xs" title="Delete" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }} onClick={() => handleDelete(item)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '16px 20px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
              <select
                className="form-control"
                style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                value={limit === 100000 ? 'all' : limit}
                onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
              >
                <option value={20}>20</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value="all">All</option>
              </select>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
          </div>
        )}
      </div>

      {showModal && (
        <AddEditModal
          stock={editTarget}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
          onSuccess={() => { setShowModal(false); setEditTarget(null); fetchList(); }}
        />
      )}

      {linksTarget && (
        <ManageLinksModal
          stock={linksTarget}
          onClose={() => setLinksTarget(null)}
          onSave={handleSaveLinks}
        />
      )}
    </div>
  );
}
